import React, { useCallback, useMemo, ReactNode } from 'react'
import { Controller, Control } from 'react-hook-form'
import {
  FormHelperText,
  FormControl,
  Checkbox,
  Autocomplete as MuiAutocomplete,
  Typography,
  Chip,
  Stack,
  Tooltip,
  AutocompleteRenderOptionState,
  FormControlProps,
  TextField
} from '@mui/material'
// @ts-expect-error - Import needs esModuleInterop flag
import { useTranslation } from '@contexts/translation'
import TextFieldItem from './Form/TextFieldItem'
import { SelectSkeleton } from './Skeletons'
// @ts-expect-error - Import needs esModuleInterop flag
import useChipStyles from '@styles/chip/chip'
import { Add } from '@mui/icons-material'

export interface OptionType {
  [key: string]: any
  disabled?: boolean
  tooltip?: string
  hidden?: boolean
  highlight?: boolean
  icon?: ReactNode
  subheader?: boolean
}

interface Elements {
  /** Array of items to display in the autocomplete */
  items: OptionType[]
  /** Field name to use as the item ID (e.g., 'id', 'pk') */
  id?: string
  /** Field name to use as the display value (e.g., 'name', 'display', 'title') */
  display?: string
  /** Field name to determine if an option is disabled */
  disabled?: string
}

interface AutocompleteItemProps extends Omit<FormControlProps, 'defaultValue'> {
  /** Field name in the form */
  name: string
  /** Label text for the field */
  label: string
  /** React Hook Form control object */
  control: Control<any>
  /** Default value for the field */
  defaultValue?: string | number | string[] | number[]
  /** Whether multiple selections are allowed */
  multiple?: boolean
  /** Whether the field has an error */
  error?: boolean
  /** Error message to display */
  errorMessage?: string
  /** Helper text to display below the field */
  helperText?: string | ReactNode
  /** Elements configuration for the autocomplete */
  elements?: Elements
  /** Whether to show a count of selected items */
  showCount?: boolean
  /** Whether to allow free text input */
  freeSolo?: boolean
  /** Whether the component is in a loading state */
  loading?: boolean
  /** Whether to show a skeleton loading state */
  skeletonLoading?: boolean
  /** Variant of the text field */
  variant?: 'filled' | 'outlined' | 'standard'
  /** Function to call when an option is selected */
  submitOnClick?: (value: any | any[]) => void
  /** Function to group options */
  groupBy?: (option: any) => string
  /** Whether the field is required */
  required?: boolean
  /** Whether the field is disabled */
  disabled?: boolean
  /** Help text to display below the field */
  helpText?: string | boolean
  /** Function to call when an option is clicked */
  onClickOption?: (e: React.MouseEvent, option: any) => void
  /** Whether to display selected items as chips */
  chipTags?: boolean
  /** Whether the field can be cleared */
  disableClearable?: boolean
  /** Text for the add option */
  addOption?: string
  /** Function to call when the add option is clicked */
  handleAdd?: () => void
  /** Whether to disable list wrapping */
  disableListWrap?: boolean
  /** Test ID for component testing */
  data_testid?: string
  /** Whether to disable the select all option */
  disableSelectAll?: boolean
  /** Function to call when the field is cleared */
  onClear?: () => void
  /** Size of the autocomplete */
  size?: 'small' | 'medium'
}

/**
 * AutocompleteItem component
 * An autocomplete component that integrates with React Hook Form
 */
const AutocompleteItem = ({
  name,
  label,
  control,
  defaultValue,
  multiple = false,
  errorMessage,
  helperText,
  elements = { items: [], id: '', display: '', disabled: '' },
  showCount = false,
  freeSolo = false,
  loading = false,
  skeletonLoading = false,
  variant = 'filled',
  submitOnClick,
  groupBy,
  required,
  disabled = false,
  helpText = false,
  onClickOption = () => null,
  chipTags,
  disableClearable = false,
  addOption,
  handleAdd,
  disableListWrap = false,
  data_testid,
  disableSelectAll = false,
  onClear,
  size = 'medium',
  ...props
}: AutocompleteItemProps): JSX.Element => {
  const { t } = useTranslation()
  const labelId = `${name}-label`
  const classesChip = useChipStyles()

  const idField = elements?.id || 'id'
  const displayField = elements?.display || 'display'

  const options = useMemo(() => {
    const preOptions = []
    if (addOption && handleAdd)
      preOptions.push({
        [idField]: 'add_item',
        [displayField]: addOption
      })
    if (multiple)
      preOptions.push({
        [idField]: 'all',
        [displayField]: t('chip_filter_selector_select_all'),
        hidden: disableSelectAll
      })
    if (elements?.items) {
      return [...preOptions, ...elements.items]
    }
    return []
  }, [
    elements,
    addOption,
    multiple,
    disableSelectAll,
    idField,
    displayField,
    t,
    handleAdd
  ])

  const getOptionLabel = useCallback(
    (option: any): string => {
      if (option[displayField]) {
        return option[displayField]
      }
      const foundElement = elements.items.find(el => el[idField] == option)
      return foundElement ? foundElement[displayField] : ''
    },
    [elements, displayField, idField]
  )

  const renderOption = useCallback(
    (
      field: any,
      props: React.HTMLAttributes<HTMLLIElement>,
      option: any,
      state: AutocompleteRenderOptionState
    ) => {
      const { selected } = state

      // @ts-expect-error - key is indeed in props
      delete props.key // to avoid console error

      return !option.hidden ? (
        <Tooltip title={option.tooltip || ''} arrow key={props.id}>
          <div>
            {option.subheader ? (
              <li tabIndex={0} style={{ padding: '6px' }}>
                <Typography>
                  <b>{option[displayField]}</b>
                </Typography>
              </li>
            ) : (
              <li
                tabIndex={0}
                role="button"
                {...props}
                onClick={(e: React.MouseEvent) => {
                  onClickOption(e, option)
                  // @ts-expect-error - onClick is in props and cannot be undefined with Autocomplete
                  props.onClick(e)
                }}
                data-testid="li-autocomplete-96yd609g44"
              >
                {multiple && option[idField] !== 'add_item' && (
                  <Checkbox
                    sx={{ mr: 1 }}
                    checked={
                      option[idField] === 'all'
                        ? field.value.length === elements.items.length
                        : selected
                    }
                    indeterminate={
                      option[idField] === 'all'
                        ? field.value.length < elements.items.length &&
                          field.value.length > 0
                        : false
                    }
                    data-testid="Checkbox-e6e3c4f1-609c-40fc-ae57-4398d68e4232"
                  />
                )}

                {option[idField] === 'add_item' ? (
                  <Stack direction="row" alignItems="center" spacing={2}>
                    <Add color="primary" />
                    <Typography color="primary">
                      {option[displayField]}
                    </Typography>
                  </Stack>
                ) : multiple && option[idField] === 'all' ? (
                  <b>{option[displayField]}</b>
                ) : (
                  <Stack direction="row" alignItems="center" spacing={2}>
                    {option.icon}

                    <Typography
                      color={option.highlight ? 'primary' : undefined}
                    >
                      {option[displayField]}
                    </Typography>
                  </Stack>
                )}
              </li>
            )}
          </div>
        </Tooltip>
      ) : undefined
    },
    [elements, idField, displayField, onClickOption, multiple]
  )

  const renderMultipleTagsWithoutChip = useCallback(
    (value: any[], element: any, multipleElements: string[] | undefined) => {
      return multipleElements ? (
        showCount ? (
          <Typography noWrap variant="subtitle2" color="text.dark">
            {element?.[displayField] +
              (value.length - 1 > 0 ? ` +${value.length - 1}` : '')}
          </Typography>
        ) : (
          <Typography noWrap variant="subtitle2" color="text.dark">
            {multipleElements.join(', ')}
          </Typography>
        )
      ) : null
    },
    [showCount, displayField]
  )

  const renderMultipleTagsWithChip = useCallback(
    (multipleElements: string[] | undefined) => {
      if (multipleElements && multipleElements.length > 2) {
        return (
          <>
            {multipleElements.slice(0, 2).map((e, index) => (
              <Chip
                key={index}
                label={e}
                size="small"
                className={classesChip.normal}
                sx={{
                  marginRight: 1,
                  paddingY: 2,
                  marginBottom: 1
                }}
              />
            ))}
            <Chip
              label={`+${multipleElements.slice(2).length}`}
              size="small"
              className={classesChip.normal}
              sx={{
                marginRight: 1,
                paddingY: 2,
                marginBottom: 1
              }}
            />
          </>
        )
      } else {
        return multipleElements?.map((e, index) => (
          <Chip
            key={index}
            label={e}
            size="small"
            className={classesChip.normal}
            sx={{
              marginRight: 1,
              paddingY: 2,
              marginBottom: 1
            }}
          />
        ))
      }
    },
    [classesChip]
  )

  const renderTags = useCallback(
    (value: any) => {
      const element = elements.items.find(el =>
        multiple ? value.includes(el[idField]) : el[idField] == value
      )
      const multipleElements = multiple
        ? elements.items
            .filter(el => value.includes(el[idField]))
            .map(el => el[displayField])
        : undefined
      return multiple
        ? chipTags
          ? renderMultipleTagsWithChip(multipleElements)
          : renderMultipleTagsWithoutChip(value, element, multipleElements)
        : elements.items.find(el => el[idField] == value)?.[displayField]
    },
    [
      elements,
      multiple,
      chipTags,
      idField,
      displayField,
      renderMultipleTagsWithChip,
      renderMultipleTagsWithoutChip
    ]
  )

  return (
    <FormControl {...props} variant="filled" margin="normal" fullWidth>
      <Controller
        name={name}
        control={control}
        defaultValue={defaultValue}
        render={({ field }) => {
          return (
            <>
              {!skeletonLoading && (
                <MuiAutocomplete
                  disableClearable={disableClearable}
                  fullWidth
                  size={size}
                  data-testid={data_testid}
                  id={labelId}
                  multiple={multiple}
                  disabled={disabled}
                  disableCloseOnSelect={multiple}
                  loading={loading}
                  groupBy={groupBy}
                  getOptionDisabled={(option: any) => {
                    if (
                      (elements?.disabled && option[elements.disabled]) ||
                      option.disabled
                    ) {
                      return true
                    }
                    return false
                  }}
                  value={
                    (freeSolo
                      ? elements.items.find(
                          (item: any) => item[idField] === field.value
                        )
                      : field.value) || null
                  }
                  onInputChange={(event, value, reason) => {
                    if (freeSolo && value) field.onChange(value)
                    else if (reason === 'clear' && onClear) onClear()
                  }}
                  onChange={(event, newValue: any) => {
                    let _newValue: any

                    if (
                      !multiple &&
                      newValue?.[idField] === 'add_item' &&
                      handleAdd
                    ) {
                      handleAdd()
                      return
                    }

                    if (multiple) {
                      if (
                        newValue
                          ?.map((v: any) => v[idField])
                          .includes('add_item') &&
                        handleAdd
                      ) {
                        handleAdd()
                        return
                      }
                      if (
                        newValue?.[newValue.length - 1]?.[idField] === 'all' ||
                        newValue?.[newValue.length - 1] === 'all'
                      ) {
                        _newValue =
                          field.value.length === elements.items.length
                            ? []
                            : elements.items.map((v: any) => v[idField])
                        field.onChange(_newValue)
                      } else {
                        _newValue = newValue.map((v: any) =>
                          v?.[idField] ? v[idField] : v
                        )

                        field.onChange(_newValue)
                      }
                    } else {
                      _newValue = newValue?.[idField] || ''
                      field.onChange(newValue?.[idField] || '')
                    }
                    if (submitOnClick) submitOnClick(_newValue)
                  }}
                  freeSolo={freeSolo}
                  isOptionEqualToValue={(option: any, value: any) => {
                    return option[idField].toString() === value.toString()
                  }}
                  options={options}
                  getOptionLabel={getOptionLabel}
                  renderOption={(props, option, state) =>
                    renderOption(field, props, option, state)
                  }
                  renderTags={value => renderTags(value)}
                  renderInput={params => (
                    <TextField
                      {...params}
                      label={label}
                      variant={variant}
                      required={required}
                      error={!!errorMessage}
                      helperText={errorMessage}
                      data-testid="TextFieldItem-875804cf-c362-4132-8c2e-cd192ef3a934"
                    />
                  )}
                  disableListWrap={disableListWrap}
                  sx={{
                    ...(disableListWrap && {
                      '& .MuiAutocomplete-inputRoot': {
                        whiteSpace: 'nowrap',
                        flexWrap: 'nowrap'
                      }
                    })
                  }}
                />
              )}

              {skeletonLoading && <SelectSkeleton />}
            </>
          )
        }}
      />
      {helpText && typeof helpText === 'string' && (
        <Typography sx={{ marginTop: 1 }}>{helpText}</Typography>
      )}
      {helperText && <FormHelperText>{helperText}</FormHelperText>}
    </FormControl>
  )
}

export default AutocompleteItem
